import React, {
  useEffect,
  useRef,
  useState,
} from "react";


const GEOMETRY_EPSILON =
  0.000000001;


function finiteNumber(
  value
) {
  const number =
    Number(value);

  return Number.isFinite(
    number
  )
    ? number
    : null;
}


function percentage(
  fraction
) {
  const value =
    Number(
      (
        fraction *
        100
      ).toFixed(
        6
      )
    );

  return `${value}%`;
}


export function normalizeChildrensBookAuthoringSurfaceGeometry(
  value
) {
  if (
    !value ||
    typeof value !==
      "object" ||
    Array.isArray(
      value
    )
  ) {
    return null;
  }

  const xFraction =
    finiteNumber(
      value.xFraction
    );

  const yFraction =
    finiteNumber(
      value.yFraction
    );

  const widthFraction =
    finiteNumber(
      value.widthFraction
    );

  const heightFraction =
    finiteNumber(
      value.heightFraction
    );

  if (
    xFraction == null ||
    yFraction == null ||
    widthFraction == null ||
    heightFraction == null
  ) {
    return null;
  }

  if (
    xFraction < 0 ||
    yFraction < 0 ||
    widthFraction <= 0 ||
    heightFraction <= 0
  ) {
    return null;
  }

  if (
    xFraction +
      widthFraction >
    1 +
      GEOMETRY_EPSILON
  ) {
    return null;
  }

  if (
    yFraction +
      heightFraction >
    1 +
      GEOMETRY_EPSILON
  ) {
    return null;
  }

  return {
    xFraction,

    yFraction,

    widthFraction,

    heightFraction,
  };
}


export function childrensBookAuthoringSurfaceBoxStyle(
  geometry
) {
  const normalized =
    normalizeChildrensBookAuthoringSurfaceGeometry(
      geometry
    );

  if (!normalized) {
    return null;
  }

  return {
    position:
      "absolute",

    left:
      percentage(
        normalized
          .xFraction
      ),

    top:
      percentage(
        normalized
          .yFraction
      ),

    width:
      percentage(
        normalized
          .widthFraction
      ),

    height:
      percentage(
        normalized
          .heightFraction
      ),

    boxSizing:
      "border-box",

    overflow:
      "hidden",
  };
}


const MIN_RESIZABLE_FRACTION =
  0.02;


function clamp(
  value,
  minimum,
  maximum
) {
  return Math.min(
    maximum,
    Math.max(
      minimum,
      value
    )
  );
}


export function resizeChildrensBookAuthoringSurfaceGeometry({
  geometry,
  deltaXFraction = 0,
  deltaYFraction = 0,
  preserveAspectRatio = true,
} = {}) {
  const normalized =
    normalizeChildrensBookAuthoringSurfaceGeometry(
      geometry
    );

  if (!normalized) {
    return null;
  }

  const deltaX =
    finiteNumber(
      deltaXFraction
    ) ?? 0;

  const deltaY =
    finiteNumber(
      deltaYFraction
    ) ?? 0;

  const maximumWidth =
    1 -
    normalized.xFraction;

  const maximumHeight =
    1 -
    normalized.yFraction;

  if (
    preserveAspectRatio
  ) {
    const widthScale =
      (
        normalized.widthFraction +
        deltaX
      ) /
      normalized.widthFraction;

    const heightScale =
      (
        normalized.heightFraction +
        deltaY
      ) /
      normalized.heightFraction;

    const dominantScale =
      Math.abs(
        widthScale -
        1
      ) >=
      Math.abs(
        heightScale -
        1
      )
        ? widthScale
        : heightScale;

    const minimumScale =
      Math.min(
        1,
        Math.max(
          MIN_RESIZABLE_FRACTION /
            normalized.widthFraction,

          MIN_RESIZABLE_FRACTION /
            normalized.heightFraction
        )
      );

    const maximumScale =
      Math.min(
        maximumWidth /
          normalized.widthFraction,

        maximumHeight /
          normalized.heightFraction
      );

    const scale =
      clamp(
        dominantScale,
        minimumScale,
        maximumScale
      );

    return normalizeChildrensBookAuthoringSurfaceGeometry({
      ...normalized,

      widthFraction:
        normalized.widthFraction *
        scale,

      heightFraction:
        normalized.heightFraction *
        scale,
    });
  }

  return normalizeChildrensBookAuthoringSurfaceGeometry({
    ...normalized,

    widthFraction:
      clamp(
        normalized.widthFraction +
          deltaX,
        Math.min(
          MIN_RESIZABLE_FRACTION,
          maximumWidth
        ),
        maximumWidth
      ),

    heightFraction:
      clamp(
        normalized.heightFraction +
          deltaY,
        Math.min(
          MIN_RESIZABLE_FRACTION,
          maximumHeight
        ),
        maximumHeight
      ),
  });
}


export function moveChildrensBookAuthoringSurfaceGeometry({
  geometry,
  deltaXFraction = 0,
  deltaYFraction = 0,
} = {}) {
  const normalized =
    normalizeChildrensBookAuthoringSurfaceGeometry(
      geometry
    );

  if (!normalized) {
    return null;
  }

  const deltaX =
    finiteNumber(
      deltaXFraction
    ) ?? 0;

  const deltaY =
    finiteNumber(
      deltaYFraction
    ) ?? 0;

  return normalizeChildrensBookAuthoringSurfaceGeometry({
    ...normalized,

    xFraction:
      clamp(
        normalized.xFraction +
          deltaX,
        0,
        1 -
          normalized.widthFraction
      ),

    yFraction:
      clamp(
        normalized.yFraction +
          deltaY,
        0,
        1 -
          normalized.heightFraction
      ),
  });
}


export default function ChildrensBookAuthoringImageSurface({
  geometry = null,
  containerAspectRatio = null,
  label =
    "Page composition preview",
  resizable = false,
  preserveAspectRatio = true,
  onGeometryCommit = null,
  children,
}) {
  const normalized =
    normalizeChildrensBookAuthoringSurfaceGeometry(
      geometry
    );

  const aspectRatio =
    finiteNumber(
      containerAspectRatio
    );

  const [
    localGeometry,
    setLocalGeometry,
  ] = useState(
    () =>
      normalized
  );

  const surfaceRef =
    useRef(null);

  const resizeGestureRef =
    useRef(null);

  const moveGestureRef =
    useRef(null);

  useEffect(
    () => {
      setLocalGeometry(
        normalized
      );
    },
    [
      normalized
        ?.xFraction,
      normalized
        ?.yFraction,
      normalized
        ?.widthFraction,
      normalized
        ?.heightFraction,
    ]
  );

  if (
    !normalized ||
    aspectRatio == null ||
    aspectRatio <= 0
  ) {
    return null;
  }

  const displayGeometry =
    (
      resizable &&
      localGeometry
    )
      ? localGeometry
      : normalized;

  const imageBoxStyle =
    childrensBookAuthoringSurfaceBoxStyle(
      displayGeometry
    );

  const movementAvailable =
    Boolean(
      resizable &&
      (
        displayGeometry
          .widthFraction <
          1 -
            GEOMETRY_EPSILON ||
        displayGeometry
          .heightFraction <
          1 -
            GEOMETRY_EPSILON
      )
    );

  function beginResize(
    event
  ) {
    if (!resizable) {
      return;
    }

    const bounds =
      surfaceRef
        .current
        ?.getBoundingClientRect?.();

    if (
      !bounds ||
      !Number.isFinite(
        bounds.width
      ) ||
      !Number.isFinite(
        bounds.height
      ) ||
      bounds.width <= 0 ||
      bounds.height <= 0
    ) {
      return;
    }

    const clientX =
      finiteNumber(
        event.clientX
      );

    const clientY =
      finiteNumber(
        event.clientY
      );

    if (
      clientX == null ||
      clientY == null
    ) {
      return;
    }

    resizeGestureRef.current = {
      pointerId:
        event.pointerId,

      startClientX:
        clientX,

      startClientY:
        clientY,

      pageWidth:
        bounds.width,

      pageHeight:
        bounds.height,

      startGeometry:
        displayGeometry,
    };

    if (
      typeof event
        .currentTarget
        ?.setPointerCapture ===
        "function" &&
      event.pointerId != null
    ) {
      event.currentTarget
        .setPointerCapture(
          event.pointerId
        );
    }

    event.preventDefault();
  }


  function continueResize(
    event
  ) {
    const gesture =
      resizeGestureRef
        .current;

    if (!gesture) {
      return;
    }

    if (
      gesture.pointerId != null &&
      event.pointerId != null &&
      gesture.pointerId !==
        event.pointerId
    ) {
      return;
    }

    const clientX =
      finiteNumber(
        event.clientX
      );

    const clientY =
      finiteNumber(
        event.clientY
      );

    if (
      clientX == null ||
      clientY == null
    ) {
      return;
    }

    const nextGeometry =
      resizeChildrensBookAuthoringSurfaceGeometry({
        geometry:
          gesture
            .startGeometry,

        deltaXFraction:
          (
            clientX -
            gesture.startClientX
          ) /
          gesture.pageWidth,

        deltaYFraction:
          (
            clientY -
            gesture.startClientY
          ) /
          gesture.pageHeight,

        preserveAspectRatio,
      });

    if (nextGeometry) {
      setLocalGeometry(
        nextGeometry
      );
    }

    event.preventDefault();
  }


  function finishResize(
    event,
    commitGeometry =
      true
  ) {
    const gesture =
      resizeGestureRef
        .current;

    if (!gesture) {
      return;
    }

    if (
      gesture.pointerId != null &&
      event.pointerId != null &&
      gesture.pointerId !==
        event.pointerId
    ) {
      return;
    }

    let finalGeometry =
      localGeometry ||
      gesture.startGeometry;

    const clientX =
      finiteNumber(
        event.clientX
      );

    const clientY =
      finiteNumber(
        event.clientY
      );

    if (
      commitGeometry &&
      clientX != null &&
      clientY != null
    ) {
      const releasedGeometry =
        resizeChildrensBookAuthoringSurfaceGeometry({
          geometry:
            gesture
              .startGeometry,

          deltaXFraction:
            (
              clientX -
              gesture.startClientX
            ) /
            gesture.pageWidth,

          deltaYFraction:
            (
              clientY -
              gesture.startClientY
            ) /
            gesture.pageHeight,

          preserveAspectRatio,
        });

      if (releasedGeometry) {
        finalGeometry =
          releasedGeometry;

        setLocalGeometry(
          releasedGeometry
        );
      }
    }

    if (
      typeof event
        .currentTarget
        ?.releasePointerCapture ===
        "function" &&
      event.pointerId != null
    ) {
      try {
        event.currentTarget
          .releasePointerCapture(
            event.pointerId
          );
      } catch (error) {
      }
    }

    resizeGestureRef.current =
      null;

    if (!commitGeometry) {
      setLocalGeometry(
        gesture.startGeometry
      );

      event.preventDefault();

      return;
    }

    const geometryChanged =
      Boolean(
        finalGeometry &&
        [
          "xFraction",
          "yFraction",
          "widthFraction",
          "heightFraction",
        ].some(
          (key) =>
            Number(
              finalGeometry?.[key]
            ) !==
            Number(
              gesture
                .startGeometry?.[key]
            )
        )
      );

    if (
      geometryChanged &&
      typeof onGeometryCommit ===
        "function"
    ) {
      onGeometryCommit(
        finalGeometry
      );
    }

    event.preventDefault();
  }


  function beginMove(
    event
  ) {
    if (!movementAvailable) {
      return;
    }

    const bounds =
      surfaceRef
        .current
        ?.getBoundingClientRect?.();

    const clientX =
      finiteNumber(
        event.clientX
      );

    const clientY =
      finiteNumber(
        event.clientY
      );

    if (
      !bounds ||
      !Number.isFinite(
        bounds.width
      ) ||
      !Number.isFinite(
        bounds.height
      ) ||
      bounds.width <= 0 ||
      bounds.height <= 0 ||
      clientX == null ||
      clientY == null
    ) {
      return;
    }

    moveGestureRef.current = {
      pointerId:
        event.pointerId,

      startClientX:
        clientX,

      startClientY:
        clientY,

      pageWidth:
        bounds.width,

      pageHeight:
        bounds.height,

      startGeometry:
        displayGeometry,
    };

    if (
      typeof event
        .currentTarget
        ?.setPointerCapture ===
        "function" &&
      event.pointerId != null
    ) {
      event.currentTarget
        .setPointerCapture(
          event.pointerId
        );
    }

    event.preventDefault();
  }


  function continueMove(
    event
  ) {
    const gesture =
      moveGestureRef
        .current;

    if (!gesture) {
      return;
    }

    if (
      gesture.pointerId != null &&
      event.pointerId != null &&
      gesture.pointerId !==
        event.pointerId
    ) {
      return;
    }

    const clientX =
      finiteNumber(
        event.clientX
      );

    const clientY =
      finiteNumber(
        event.clientY
      );

    if (
      clientX == null ||
      clientY == null
    ) {
      return;
    }

    const nextGeometry =
      moveChildrensBookAuthoringSurfaceGeometry({
        geometry:
          gesture.startGeometry,

        deltaXFraction:
          (
            clientX -
            gesture.startClientX
          ) /
          gesture.pageWidth,

        deltaYFraction:
          (
            clientY -
            gesture.startClientY
          ) /
          gesture.pageHeight,
      });

    if (nextGeometry) {
      setLocalGeometry(
        nextGeometry
      );
    }

    event.preventDefault();
  }


  function finishMove(
    event,
    commitGeometry =
      true
  ) {
    const gesture =
      moveGestureRef
        .current;

    if (!gesture) {
      return;
    }

    if (
      gesture.pointerId != null &&
      event.pointerId != null &&
      gesture.pointerId !==
        event.pointerId
    ) {
      return;
    }

    const clientX =
      finiteNumber(
        event.clientX
      );

    const clientY =
      finiteNumber(
        event.clientY
      );

    let finalGeometry =
      localGeometry ||
      gesture.startGeometry;

    if (
      commitGeometry &&
      clientX != null &&
      clientY != null
    ) {
      const releasedGeometry =
        moveChildrensBookAuthoringSurfaceGeometry({
          geometry:
            gesture.startGeometry,

          deltaXFraction:
            (
              clientX -
              gesture.startClientX
            ) /
            gesture.pageWidth,

          deltaYFraction:
            (
              clientY -
              gesture.startClientY
            ) /
            gesture.pageHeight,
        });

      if (releasedGeometry) {
        finalGeometry =
          releasedGeometry;

        setLocalGeometry(
          releasedGeometry
        );
      }
    }

    if (
      typeof event
        .currentTarget
        ?.releasePointerCapture ===
        "function" &&
      event.pointerId != null
    ) {
      try {
        event.currentTarget
          .releasePointerCapture(
            event.pointerId
          );
      } catch (error) {
      }
    }

    moveGestureRef.current =
      null;

    if (!commitGeometry) {
      setLocalGeometry(
        gesture.startGeometry
      );

      event.preventDefault();
      return;
    }

    const geometryChanged =
      Boolean(
        finalGeometry &&
        (
          Number(
            finalGeometry
              .xFraction
          ) !==
          Number(
            gesture
              .startGeometry
              ?.xFraction
          ) ||
          Number(
            finalGeometry
              .yFraction
          ) !==
          Number(
            gesture
              .startGeometry
              ?.yFraction
          )
        )
      );

    if (
      geometryChanged &&
      typeof onGeometryCommit ===
        "function"
    ) {
      onGeometryCommit(
        finalGeometry
      );
    }

    event.preventDefault();
  }


  return (
    <div
      ref={surfaceRef}
      role="group"
      aria-label={label}
      data-testid="book-structure-authoring-page-surface"
      data-resizable={
        resizable
          ? "true"
          : "false"
      }
      data-container-aspect-ratio={
        aspectRatio
      }
      style={{
        position:
          "relative",

        width:
          "100%",

        aspectRatio:
          String(
            aspectRatio
          ),

        overflow:
          "hidden",

        boxSizing:
          "border-box",

        border:
          "1px solid #94a3b8",

        borderRadius:
          12,

        background:
          "#ffffff",
      }}
    >
      <div
        data-testid="book-structure-authoring-image-box"
        data-x-fraction={
          displayGeometry
            .xFraction
        }
        data-y-fraction={
          displayGeometry
            .yFraction
        }
        data-width-fraction={
          displayGeometry
            .widthFraction
        }
        data-height-fraction={
          displayGeometry
            .heightFraction
        }
        style={
          imageBoxStyle
        }
      >
        <div
          data-testid="book-structure-authoring-image-content"
          data-movable={
            movementAvailable
              ? "true"
              : "false"
          }
          onPointerDown={
            beginMove
          }
          onPointerMove={
            continueMove
          }
          onPointerUp={
            (event) =>
              finishMove(
                event,
                true
              )
          }
          onPointerCancel={
            (event) =>
              finishMove(
                event,
                false
              )
          }
          onDragStart={
            (event) =>
              event.preventDefault()
          }
          style={{
            width:
              "100%",

            height:
              "100%",

            overflow:
              "hidden",

            cursor:
              movementAvailable
                ? "move"
                : "default",

            touchAction:
              movementAvailable
                ? "none"
                : "auto",
          }}
        >
          {children}
        </div>

        {resizable ? (
          <button
            type="button"
            aria-label="Resize illustration"
            data-testid="book-structure-authoring-image-resize-handle"
            onPointerDown={
              beginResize
            }
            onPointerMove={
              continueResize
            }
            onPointerUp={
              (event) =>
                finishResize(
                  event,
                  true
                )
            }
            onPointerCancel={
              (event) =>
                finishResize(
                  event,
                  false
                )
            }
            style={{
              position:
                "absolute",

              right:
                4,

              bottom:
                4,

              width:
                24,

              height:
                24,

              padding:
                0,

              borderRadius:
                6,

              border:
                "2px solid #0f172a",

              background:
                "#ffffff",

              boxShadow:
                "0 1px 4px rgba(15,23,42,0.24)",

              cursor:
                "nwse-resize",

              touchAction:
                "none",

              zIndex:
                4,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
